/** @jsxImportSource @emotion/react */

import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Avatar, Box, Dialog, DialogActions, DialogContent, Divider, Popover } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import { QSButton, QSTypography } from '../../../../../shared/components/atoms';
import { QSCheckbox } from '../../../../../shared/components/atoms/form';
import Loader from '../../../../../shared/components/molecules/loader';
import QSSearchBar from '../../../../../shared/components/molecules/search-bar';
import CallAllowIcon from '../../../../../shared/icons/icons-revamp/call_allow';
import useNotifications from '../../../../../shared/hooks/use-notifications';
import useQuery from '../../../../../shared/hooks/useQuery';
import { getAvatarText } from '../../../../../shared/utils/generate-avatar-text';
import { teamMembersAtom } from '../../../../home/v1/states/team-members';
import { getTeamMembers } from '../../../../home/v1/requests/get-team-members';
import { FreeTrialStep } from './typings';
import { freeTrialStepAtom, startTrialAtom } from './states';
import { assignCallLicenses } from './requests';
import { TeamMember } from '../../../../home/v1/typings';
import { ExpandMore } from '@mui/icons-material';

const AVATAR_SX = (index: number) => ({
  width: 28,
  height: 28,
  fontSize: '11px',
  fontWeight: 600,
  bgcolor: '#596bd714',
  color: '#596bd7',
  flexShrink: 0,
  border: '2px solid #fff',
  boxSizing: 'border-box',
  marginLeft: index === 0 ? 0 : -0.6,
});

export default function GrantAccessModal() {
  const [, setStep] = useAtom(freeTrialStepAtom);
  const startTrialData = useAtomValue(startTrialAtom);

  const { requestData: teamMembersRequestData } = useQuery<TeamMember[]>({
    requestAtom: teamMembersAtom,
    queryFunction: getTeamMembers,
    getCachedData: true,
  });

  const { addNotification } = useNotifications();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isGranting, setIsGranting] = useState(false);

  const anchorRef = useRef<HTMLDivElement | null>(null);

  const teamMembers = teamMembersRequestData.data ?? [];

  const filteredMembers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const nonBots = teamMembers.filter((m) => m.role !== 'BOT_USER');
    return term ? nonBots.filter((m) => (m.name ?? m.phone ?? '').toLowerCase().includes(term)) : nonBots;
  }, [teamMembers, searchTerm]);

  const selectedMembers = useMemo(() => teamMembers.filter((m) => selectedIds.has(m.id)), [teamMembers, selectedIds]);

  const handleToggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const onClose = useCallback(() => {
    setStep(FreeTrialStep.Closed);
  }, [setStep]);

  const onDoItLater = useCallback(() => {
    onClose();
  }, [onClose]);

  const onGrantAccess = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setIsGranting(true);
    try {
      await assignCallLicenses({ licenses: Array.from(selectedIds).map((id) => ({ userId: id })) });
      addNotification({ message: 'Calling access granted successfully', type: 'success' });
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'Failed to grant access. Please try again.',
        type: 'error',
      });
    } finally {
      setIsGranting(false);
    }
  }, [selectedIds, addNotification, onClose]);

  const quantity = startTrialData.data?.quantity ?? 5;

  const renderAvatarPreview = () => {
    const MAX_VISIBLE = 3;
    const visible = selectedMembers.slice(0, MAX_VISIBLE);
    const overflow = selectedMembers.length - MAX_VISIBLE;

    return (
      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        {visible.map((m, i) => (
          <Avatar key={m.id} sx={AVATAR_SX(i)}>
            {getAvatarText(m.name ?? m.phone ?? '')}
          </Avatar>
        ))}
        {overflow > 0 && (
          <QSTypography
            css={css`
              font-family: Inter, sans-serif;
              font-size: 13px;
              font-weight: 500;
              color: #7a7a7a;
              margin-left: 4px;
            `}
          >
            +{overflow}
          </QSTypography>
        )}
      </div>
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          width: '550px',
          maxWidth: '100%',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        css={css`
          padding: 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        `}
      >
        <div
          css={css`
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background-color: #edf7f2;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 8px;
          `}
        >
          <CallAllowIcon width={24} height={24} color="#3d906d" />
        </div>

        <QSTypography
          css={css`
            font-size: 16px;
            font-weight: 600;
            color: #2d2d2d;
            text-align: center;
            line-height: 140%;
          `}
        >
          Give team members calling access
        </QSTypography>

        <QSTypography
          css={css`
            font-size: 14px;
            font-weight: 400;
            color: #7a7a7a;
            text-align: center;
            line-height: 140%;
          `}
        >
          You have {quantity} calling licenses ready to use. Grant license access to your team members so they can start
          calling immediately!
        </QSTypography>

        <div
          css={css`
            width: 100%;
            margin-top: 8px;
          `}
        >
          <QSTypography
            css={css`
              font-family: Inter, sans-serif;
              font-size: 13px;
              font-weight: 500;
              color: #2d2d2d;
              margin-bottom: 8px;
            `}
          >
            Select team members
          </QSTypography>

          <div
            ref={anchorRef}
            css={css`
              width: 100%;
              border: 1px solid #e0e0e9;
              border-radius: 8px;
              padding: 10px 12px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              cursor: pointer;
              min-height: 44px;
              background: #fff;
              &:hover {
                border-color: #c0c0c9;
              }
            `}
            onClick={() => setIsPopoverOpen(true)}
          >
            {selectedIds.size === 0 ? (
              <QSTypography
                css={css`
                  font-family: Inter, sans-serif;
                  font-size: 13px;
                  font-weight: 400;
                  color: #7a7a7a;
                `}
              >
                Search team members...
              </QSTypography>
            ) : (
              renderAvatarPreview()
            )}
            <ExpandMore sx={{ color: '#7a7a7a', fontSize: '20px', flexShrink: 0 }} />
          </div>

          <Popover
            open={isPopoverOpen}
            onClose={() => {
              setIsPopoverOpen(false);
              setSearchTerm('');
            }}
            anchorEl={anchorRef.current}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                border: '1px solid #1e293b24',
                boxShadow: '0 2px 4px -1px rgba(0,0,0,0.06), 0 4px 6px -1px rgba(0,0,0,0.10)',
                width: anchorRef.current?.offsetWidth ?? 376,
              },
            }}
          >
            <Box sx={{ borderBottom: '1px solid #1e293b24', padding: '6px 12px' }}>
              <QSSearchBar
                onChange={(e) => setSearchTerm(e)}
                value={searchTerm}
                showSearchIcon
                placeholder="Search by team member name"
                searchIconProps={{ size: 16, color: '#7a7a7a' }}
                closeIconSx={{ height: '20px', width: '20px', svg: { height: '16px', width: '16px' } }}
              />
            </Box>

            {teamMembersRequestData.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                <Loader size={20} />
              </Box>
            ) : (
              <Virtuoso
                style={{ height: '200px' }}
                overscan={300}
                data={filteredMembers}
                itemContent={(_, member) => (
                  <Box
                    key={member.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                    onClick={() => handleToggle(member.id)}
                  >
                    <QSCheckbox size="small" checked={selectedIds.has(member.id)} />
                    <Avatar sx={AVATAR_SX(0)}>{getAvatarText(member.name ?? member.phone ?? '')}</Avatar>
                    <QSTypography
                      css={css`
                        font-family: Inter, sans-serif;
                        font-size: 13px;
                        font-weight: 500;
                        color: #2d2d2d;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                      `}
                    >
                      {member.name ?? member.phone ?? ''}
                    </QSTypography>
                  </Box>
                )}
              />
            )}
          </Popover>
        </div>
      </DialogContent>

      <Divider />

      <DialogActions
        css={css`
          padding: 8px 12px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        `}
      >
        <QSButton
          css={css`
            height: 40px;
            font-size: 14px;
            background: transparent;
            box-shadow: none;
            color: #3d906d;
            border: none;
            font-weight: 600;
            :hover {
              background: #f0faf5;
              box-shadow: none;
            }
          `}
          onClick={onDoItLater}
          disabled={isGranting}
        >
          Do it later
        </QSButton>
        <QSButton
          css={css`
            height: 40px;
            min-width: 120px;
            font-size: 14px;
            font-weight: 600;
          `}
          onClick={onGrantAccess}
          disabled={selectedIds.size === 0 || isGranting}
        >
          {isGranting ? <Loader size={16} loaderProps={{ sx: { color: 'white' } }} /> : 'Grant access'}
        </QSButton>
      </DialogActions>
    </Dialog>
  );
}
